import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

// Search Appointments - Uses standard appointments endpoint with filters
export const searchAppointments = createAsyncThunk(
  'appointmentSearch/search',
  async (searchParams, { rejectWithValue }) => {
    try {
      // Build query params from search filters
      const params = {};
      if (searchParams.q) params.search = searchParams.q;
      if (searchParams.status) params.status = searchParams.status;
      if (searchParams.date_from) params.scheduled_date__gte = searchParams.date_from;
      if (searchParams.date_to) params.scheduled_date__lte = searchParams.date_to;
      if (searchParams.doctor_id) params.doctor = searchParams.doctor_id;
      if (searchParams.patient_id) params.patient = searchParams.patient_id;
      if (searchParams.page) params.page = searchParams.page;
      
      const response = await axiosInstance.get('/appointments/', { params });
      
      console.log('🔍 Search Appointments Response:', response.data);
      
      // Handle both paginated and non-paginated responses
      const results = response.data.results || response.data;
      const count = response.data.count !== undefined ? response.data.count : (Array.isArray(results) ? results.length : 0);
      const pageSize = response.data.results ? (response.data.results.length || 10) : 10;
      const currentPage = searchParams.page || 1;
      
      // Normalize appointments to ensure doctor, patient, clinic are IDs, not objects
      const normalizeAppointment = (appt) => {
        if (!appt) return appt;
        const normalized = { ...appt };
        // Ensure doctor is an ID
        if (normalized.doctor && typeof normalized.doctor === 'object') {
          normalized.doctor = normalized.doctor?.id || normalized.doctor_id || normalized.doctor;
        }
        // Ensure patient is an ID
        if (normalized.patient && typeof normalized.patient === 'object') {
          normalized.patient = normalized.patient?.id || normalized.patient_id || normalized.patient;
        }
        // Ensure clinic is an ID or null
        if (normalized.clinic && typeof normalized.clinic === 'object') {
          normalized.clinic = normalized.clinic?.id || normalized.clinic_id || normalized.clinic;
        } else if (!normalized.clinic) {
          normalized.clinic = normalized.clinic_id || null;
        }
        return normalized;
      };
      
      const appointments = Array.isArray(results) ? results.map(normalizeAppointment) : [];
      
      console.log('📊 Processed Appointments:', {
        count: appointments.length,
        total: count,
        pagination: {
          total_count: count,
          page_size: pageSize,
          current_page: currentPage,
          total_pages: Math.ceil(count / pageSize),
          has_next: response.data.next !== null,
          has_previous: response.data.previous !== null
        }
      });
      
      return {
        appointments: appointments,
        pagination: {
          total_count: count,
          page_size: pageSize,
          current_page: currentPage,
          total_pages: Math.ceil(count / pageSize) || 1,
          has_next: response.data.next !== null,
          has_previous: response.data.previous !== null
        }
      };
    } catch (error) {
      console.error('Search appointments error:', error);
      return rejectWithValue(
        error.response?.data?.detail || error.response?.data || 'Failed to search appointments'
      );
    }
  }
);

// Get Appointment History - Uses standard appointments endpoint filtered by date
export const fetchAppointmentHistory = createAsyncThunk(
  'appointmentSearch/history',
  async (params, { rejectWithValue }) => {
    try {
      // Build query params
      const queryParams = {};
      if (params.search) queryParams.search = params.search;
      if (params.status && params.status !== 'All') queryParams.status = params.status;
      if (params.date_from) queryParams.scheduled_date__gte = params.date_from;
      if (params.limit) queryParams.page_size = params.limit;
      if (params.offset !== undefined) {
        const page = Math.floor(params.offset / (params.limit || 10)) + 1;
        queryParams.page = page;
      }
      
      // Order by date descending to show most recent first
      queryParams.ordering = '-scheduled_date,-scheduled_time';
      
      const response = await axiosInstance.get('/appointments/', { params: queryParams });
      
      console.log('📜 History Response:', response.data);
      
      const results = response.data.results || response.data;
      const count = response.data.count !== undefined ? response.data.count : (Array.isArray(results) ? results.length : 0);
      
      // Normalize appointments to ensure doctor, patient, clinic are IDs, not objects
      const normalizeAppointment = (appt) => {
        if (!appt) return appt;
        const normalized = { ...appt };
        // Ensure doctor is an ID
        if (normalized.doctor && typeof normalized.doctor === 'object') {
          normalized.doctor = normalized.doctor?.id || normalized.doctor_id || normalized.doctor;
        }
        // Ensure patient is an ID
        if (normalized.patient && typeof normalized.patient === 'object') {
          normalized.patient = normalized.patient?.id || normalized.patient_id || normalized.patient;
        }
        // Ensure clinic is an ID or null
        if (normalized.clinic && typeof normalized.clinic === 'object') {
          normalized.clinic = normalized.clinic?.id || normalized.clinic_id || normalized.clinic;
        } else if (!normalized.clinic) {
          normalized.clinic = normalized.clinic_id || null;
        }
        return normalized;
      };
      
      const appointments = Array.isArray(results) ? results.map(normalizeAppointment) : [];
      
      console.log('📊 Processed History:', {
        count: appointments.length,
        total: count
      });
      
      return {
        appointments: appointments,
        results: appointments,
        count: count,
        pagination: {
          total_count: count,
          page_size: params.limit || 10,
          current_page: params.offset ? Math.floor(params.offset / (params.limit || 10)) + 1 : 1,
          total_pages: Math.ceil(count / (params.limit || 10)) || 1,
          has_next: response.data.next !== null,
          has_previous: response.data.previous !== null
        }
      };
    } catch (error) {
      console.error('Fetch history error:', error);
      return rejectWithValue(
        error.response?.data?.detail || error.response?.data || 'Failed to fetch appointment history'
      );
    }
  }
);

// Get Appointment Analytics - Calculates analytics from appointments
export const fetchAppointmentAnalytics = createAsyncThunk(
  'appointmentSearch/analytics',
  async (params, { rejectWithValue }) => {
    try {
      // Fetch appointments for analytics
      const queryParams = {};
      
      // Filter by date range
      if (params.period_days) {
        const dateFrom = new Date();
        dateFrom.setDate(dateFrom.getDate() - params.period_days);
        queryParams.scheduled_date__gte = dateFrom.toISOString().split('T')[0];
      }
      
      // Filter by user role
      // Note: For doctors, we need to get the Doctor ID, not the User ID
      // The backend filters by doctor__user for doctors, so we pass user_id as doctor__user
      // But the filterset_fields expects 'doctor' (the Doctor ID), not 'doctor__user'
      // So we need to either:
      // 1. Use doctor__user filter (if supported)
      // 2. Fetch doctor ID from user_id first
      // For now, let's not filter by doctor if user_role is 'doctor' - the backend should handle this via authentication
      if (params.user_role === 'patient' && params.user_id) {
        queryParams.patient = params.user_id;
      }
      // For doctors, the backend's get_queryset should already filter by authenticated doctor
      // So we don't need to pass doctor filter explicitly
      
      queryParams.page_size = 1000; // Get enough data for analytics
      
      const response = await axiosInstance.get('/appointments/', { params: queryParams });
      
      console.log('📈 Analytics Response:', response.data);
      
      const appointments = response.data.results || response.data || [];
      
      // Normalize appointments to ensure doctor, patient, clinic are IDs, not objects
      const normalizeAppointment = (appt) => {
        if (!appt) return appt;
        const normalized = { ...appt };
        // Ensure doctor is an ID
        if (normalized.doctor && typeof normalized.doctor === 'object') {
          normalized.doctor = normalized.doctor?.id || normalized.doctor_id || normalized.doctor;
        }
        // Ensure patient is an ID
        if (normalized.patient && typeof normalized.patient === 'object') {
          normalized.patient = normalized.patient?.id || normalized.patient_id || normalized.patient;
        }
        // Ensure clinic is an ID or null
        if (normalized.clinic && typeof normalized.clinic === 'object') {
          normalized.clinic = normalized.clinic?.id || normalized.clinic_id || normalized.clinic;
        } else if (!normalized.clinic) {
          normalized.clinic = normalized.clinic_id || null;
        }
        return normalized;
      };
      
      const appointmentsList = Array.isArray(appointments) ? appointments.map(normalizeAppointment) : [];
      
      console.log('📊 Processing Analytics for', appointmentsList.length, 'appointments');
      
      // Calculate analytics
      const totalAppointments = appointmentsList.length;
      
      // Status distribution
      const statusCounts = {};
      appointmentsList.forEach(apt => {
        const status = apt.status || 'Unknown';
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      });
      
      const statusDistribution = Object.entries(statusCounts).map(([status, count]) => ({
        status,
        count
      }));
      
      // Type distribution
      const typeCounts = {};
      appointmentsList.forEach(apt => {
        const type = apt.type || apt.consultation_type || 'Consultation';
        typeCounts[type] = (typeCounts[type] || 0) + 1;
      });
      
      const typeDistribution = Object.entries(typeCounts).map(([type, count]) => ({
        type,
        count
      }));
      
      // Revenue calculation
      const totalRevenue = appointmentsList.reduce((sum, apt) => {
        return sum + (parseFloat(apt.consultation_fee) || 0);
      }, 0);
      
      // Appointments over time (group by day)
      const appointmentsByDay = {};
      appointmentsList.forEach(apt => {
        if (apt.scheduled_date) {
          const day = apt.scheduled_date.split('T')[0] || apt.scheduled_date;
          appointmentsByDay[day] = (appointmentsByDay[day] || 0) + 1;
        }
      });
      
      const appointmentsOverTime = Object.entries(appointmentsByDay)
        .map(([day, count]) => ({ day, count }))
        .sort((a, b) => a.day.localeCompare(b.day));
      
      return {
        total_appointments: totalAppointments,
        status_distribution: statusDistribution,
        type_distribution: typeDistribution,
        total_revenue: totalRevenue,
        appointments_over_time: appointmentsOverTime
      };
    } catch (error) {
      console.error('Fetch analytics error:', error);
      
      // Extract meaningful error message from Django validation errors
      let errorMessage = 'Failed to fetch appointment analytics';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle Django REST Framework validation errors (400 Bad Request)
        if (error.response.status === 400) {
          // Check for field-level validation errors
          if (typeof errorData === 'object' && !errorData.detail && !errorData.message) {
            // Extract first field error
            const fieldErrors = Object.keys(errorData);
            if (fieldErrors.length > 0) {
              const firstField = fieldErrors[0];
              const fieldError = errorData[firstField];
              if (Array.isArray(fieldError) && fieldError.length > 0) {
                errorMessage = `${firstField}: ${fieldError[0]}`;
              } else if (typeof fieldError === 'string') {
                errorMessage = `${firstField}: ${fieldError}`;
              } else {
                errorMessage = `Invalid ${firstField} parameter`;
              }
            }
          } else if (errorData.detail) {
            errorMessage = typeof errorData.detail === 'string' ? errorData.detail : 'Invalid request parameters';
          } else if (errorData.message) {
            errorMessage = typeof errorData.message === 'string' ? errorData.message : 'Invalid request parameters';
          }
        } else if (errorData.detail) {
          errorMessage = typeof errorData.detail === 'string' ? errorData.detail : 'An error occurred';
        } else if (errorData.message) {
          errorMessage = typeof errorData.message === 'string' ? errorData.message : 'An error occurred';
        }
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

const appointmentSearchSlice = createSlice({
  name: 'appointmentSearch',
  initialState: {
    searchResults: [],
    history: [],
    analytics: null,
    searchFilters: {
      q: '',
      status: '',
      date_from: '',
      date_to: '',
      doctor_id: '',
      patient_id: ''
    },
    pagination: {
      total_count: 0,
      page_size: 50,
      current_page: 1,
      total_pages: 0,
      has_next: false,
      has_previous: false
    },
    loading: false,
    error: null
  },
  reducers: {
    setSearchFilters: (state, action) => {
      state.searchFilters = { ...state.searchFilters, ...action.payload };
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.pagination = {
        total_count: 0,
        page_size: 50,
        current_page: 1,
        total_pages: 0,
        has_next: false,
        has_previous: false
      };
    },
    resetSearchState: (state) => {
      state.searchResults = [];
      state.history = [];
      state.analytics = null;
      state.searchFilters = {
        q: '',
        status: '',
        date_from: '',
        date_to: '',
        doctor_id: '',
        patient_id: ''
      };
      state.pagination = {
        total_count: 0,
        page_size: 50,
        current_page: 1,
        total_pages: 0,
        has_next: false,
        has_previous: false
      };
      state.loading = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Search Appointments
      .addCase(searchAppointments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchAppointments.fulfilled, (state, action) => {
        state.loading = false;
        // Normalize appointments one more time in reducer as safety measure
        const normalizeAppointment = (appt) => {
          if (!appt) return appt;
          const normalized = { ...appt };
          if (normalized.doctor && typeof normalized.doctor === 'object') {
            normalized.doctor = normalized.doctor?.id || normalized.doctor_id || normalized.doctor;
          }
          if (normalized.patient && typeof normalized.patient === 'object') {
            normalized.patient = normalized.patient?.id || normalized.patient_id || normalized.patient;
          }
          if (normalized.clinic && typeof normalized.clinic === 'object') {
            normalized.clinic = normalized.clinic?.id || normalized.clinic_id || normalized.clinic;
          } else if (!normalized.clinic) {
            normalized.clinic = normalized.clinic_id || null;
          }
          return normalized;
        };
        
        const results = action.payload?.appointments || action.payload?.results || [];
        state.searchResults = Array.isArray(results) ? results.map(normalizeAppointment) : [];
        state.pagination = action.payload?.pagination || state.pagination;
      })
      .addCase(searchAppointments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch History
      .addCase(fetchAppointmentHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppointmentHistory.fulfilled, (state, action) => {
        state.loading = false;
        // Aggressive normalization to prevent objects from being stored
        const normalizeAppointment = (appt) => {
          if (!appt) return appt;
          // Deep clone to avoid mutating
          let normalized = {};
          try {
            normalized = JSON.parse(JSON.stringify(appt));
          } catch (e) {
            normalized = { ...appt };
          }
          
          // Force doctor to ID only
          if (normalized.doctor) {
            if (typeof normalized.doctor === 'object') {
              normalized.doctor = normalized.doctor?.id || normalized.doctor_id || normalized.doctor?.pk || null;
              // If still object after extraction, force to null
              if (typeof normalized.doctor === 'object') {
                console.warn('[Redux] Doctor field is still an object, forcing to null:', normalized.doctor);
                normalized.doctor = null;
              }
            }
          } else {
            normalized.doctor = normalized.doctor_id || null;
          }
          
          // Force patient to ID only
          if (normalized.patient) {
            if (typeof normalized.patient === 'object') {
              normalized.patient = normalized.patient?.id || normalized.patient_id || normalized.patient?.pk || null;
              if (typeof normalized.patient === 'object') {
                console.warn('[Redux] Patient field is still an object, forcing to null:', normalized.patient);
                normalized.patient = null;
              }
            }
          } else {
            normalized.patient = normalized.patient_id || null;
          }
          
          // Force clinic to ID or null
          if (normalized.clinic) {
            if (typeof normalized.clinic === 'object') {
              normalized.clinic = normalized.clinic?.id || normalized.clinic_id || normalized.clinic?.pk || null;
              if (typeof normalized.clinic === 'object') {
                console.warn('[Redux] Clinic field is still an object, forcing to null:', normalized.clinic);
                normalized.clinic = null;
              }
            }
          } else {
            normalized.clinic = normalized.clinic_id || null;
          }
          
          // Ensure name fields are strings
          if (normalized.doctor_name && typeof normalized.doctor_name === 'object') {
            normalized.doctor_name = normalized.doctor_name?.name || normalized.doctor_name?.user?.name || 'Unknown Doctor';
          }
          if (normalized.patient_name && typeof normalized.patient_name === 'object') {
            normalized.patient_name = normalized.patient_name?.name || 'Unknown Patient';
          }
          
          return normalized;
        };
        
        const payload = { ...action.payload };
        if (payload.results && Array.isArray(payload.results)) {
          payload.results = payload.results.map(normalizeAppointment);
        }
        if (payload.appointments && Array.isArray(payload.appointments)) {
          payload.appointments = payload.appointments.map(normalizeAppointment);
        }
        
        state.history = payload;
        state.pagination = payload?.pagination || state.pagination;
      })
      .addCase(fetchAppointmentHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Analytics
      .addCase(fetchAppointmentAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppointmentAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.analytics = action.payload;
      })
      .addCase(fetchAppointmentAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { setSearchFilters, clearSearchResults, resetSearchState } = appointmentSearchSlice.actions;
export default appointmentSearchSlice.reducer;